<template>
<div class="contents_855">
  <header-bar active="wash-request-currents"></header-bar>

  <h1 class="title2">{{ $t('washRequestBagCountChange.inProgressWashRequest') }}</h1>

  <div class="contents_box" v-show="currentStep === step.EDIT">
    <p class="title3">{{ $t('washRequestBagCountChange.bagCountChange') }}</p>
    <table class="form_tab form_line_top">
      <tr class="count-row-before">
        <th class="form_line_l">
          <p class="item2">{{ $t('washRequestBagCountChange.before') }}</p>
        </th>
        <td class="form_line_r">
          <div class="count-cell-content">
            <span class="count-cell-text">{{ originalCount }}{{ $t('washRequest.unit') }}</span>
          </div>
        </td>
      </tr>
      <tr class="count-row-after">
        <th class="form_line_l">
          <p class="item2">{{ $t('washRequestBagCountChange.after') }}</p>
        </th>
        <td class="form_line_r">
          <div class="count-cell-content">
            <select v-model="selectedCount" class="w55" :disabled="!canChangeCount">
              <option value=""></option>
              <option v-for="count in countOptions" :key="count" :value="count">
                {{ count }}
              </option>
            </select>
            <span class="count-cell-text">{{ $t('washRequest.unit') }}</span>
          </div>
        </td>
      </tr>
    </table>

    <div class="err-msg-area">
      <span class="font_orange" v-show="showWithdrawnError">
        {{ $t('accountWithdrawn') }}
      </span>
      <span class="font_orange" v-for="serverError in serverErrors" :key="serverError">
        {{ serverError }}
      </span>
    </div>

    <p class="submit_bx_l">
      <router-link :to="{ name: 'wash-request-currents' }" class="submit1 bg_gray">
        {{ $t('backButton') }}
      </router-link>
    </p>
    <p class="submit_bx_r">
      <button class="submit1 bg_green confirm-button" :disabled="isConfirmDisabled" @click="goToConfirm">
        {{ $t('confirmButton') }}
      </button>
    </p>
  </div>

  <div class="contents_box" v-show="currentStep === step.CONFIRM">
    <p class="mypage_notes mg30 mg_bt30">
      <span class="font_orange">
        {{ $t('washRequestBagCountChange.confirmMessage') }}
      </span>
    </p>

    <p class="title3">{{ $t('washRequestBagCountChange.bagCountChange') }}</p>
    <table class="form_tab form_line_top">
      <tr class="count-row-before">
        <th class="form_line_l">
          <p class="item2">{{ $t('washRequestBagCountChange.before') }}</p>
        </th>
        <td class="form_line_r">
          <div class="count-cell-content">
            <span class="count-cell-text">{{ originalCount }}{{ $t('washRequest.unit') }}</span>
          </div>
        </td>
      </tr>
      <tr class="count-row-after">
        <th class="form_line_l">
          <p class="item2">{{ $t('washRequestBagCountChange.after') }}</p>
        </th>
        <td class="form_line_r">
          <div class="count-cell-content">
            <span class="count-cell-text">{{ selectedCount }}{{ $t('washRequest.unit') }}</span>
          </div>
        </td>
      </tr>
    </table>

    <div class="err-msg-area">
      <span class="font_orange" v-show="showWithdrawnError">
        {{ $t('accountWithdrawn') }}
      </span>
      <span class="font_orange" v-for="serverError in serverErrors" :key="serverError">
        {{ serverError }}
      </span>
    </div>

    <p class="submit_bx_l">
      <button class="submit1 bg_gray" @click="backToEdit">
        {{ $t('backButton') }}
      </button>
    </p>
    <p class="submit_bx_r">
      <button class="submit1 bg_green" :disabled="isSubmitting" @click="submit">
        {{ $t('okButton') }}
      </button>
    </p>
  </div>

  <div class="contents_box" v-show="currentStep === step.COMPLETE">
    <p class="title3">{{ $t('washRequestBagCountChange.bagCountChange') }}</p>
    <table class="form_tab count-complete-table">
      <tr class="count-row-complete">
        <td class="count-complete-cell" colspan="2">
          <p class="complete-message">{{ $t('washRequestBagCountChange.completeMessage') }}</p>
        </td>
      </tr>
    </table>

    <p class="submit_bx1">
      <router-link :to="{ name: 'wash-request-currents' }" class="submit1 bg_green">
        {{ $t('washRequestBagCountChange.backToCurrents') }}
      </router-link>
    </p>
  </div>
</div>
</template>

<script>
import {
  computed,
  defineComponent,
  getCurrentInstance,
  onMounted,
  ref
} from 'vue'
import washRequestMixin from '@/mixin/washRequestMixin'
import userApi from '@/api/user'
import store from '@/store/index'
import {
  useWashRequestFieldChange,
  WASH_REQUEST_FIELD_CHANGE_STEP,
} from '@/composables/useWashRequestFieldChange'

const MAX_WASH_REQUEST_COUNT = 10

export default defineComponent({
  name: 'wash-request-bag-count-change',
  mixins: [washRequestMixin],
  props: {
    id: {
      type: [String, Number],
      default: -1,
    },
  },
  setup(props) {
    const vue = getCurrentInstance().proxy
    const originalCount = ref(1)
    const selectedCount = ref('')
    const countOptions = Array.from({ length: MAX_WASH_REQUEST_COUNT }, (_, index) => index + 1)

    const canChangeCount = computed(() =>
      !!(currentWashRequest.value && currentWashRequest.value.canChangeWashRequestCount))
    const selectedCountNumber = computed(() => {
      if (selectedCount.value === '' || selectedCount.value === null) {
        return null
      }
      return Number(selectedCount.value)
    })
    const isConfirmDisabled = computed(() => {
      if (!canChangeCount.value) {
        return true
      }
      if (selectedCountNumber.value === null) {
        return true
      }
      return selectedCountNumber.value === originalCount.value
    })

    const {
      isSubmitting,
      currentStep,
      currentWashRequest,
      showWithdrawnError,
      serverErrors,
      fetchCurrentWashRequest,
      goToConfirm,
      backToEdit,
      submit,
    } = useWashRequestFieldChange({
      getWashRequestId: () => props.id,
      convertWashRequest: (data) => vue.convWashReq_(data, 0, new Date()),
      applyFetchedWashRequest: (convertedWashRequest) => {
        originalCount.value = Number(convertedWashRequest.count)
        selectedCount.value = ''
      },
      callUpdateApi: () => userApi.updateWashRequestBagCount(currentWashRequest.value.id, {
        bagCount: selectedCountNumber.value,
      }),
      isConfirmDisabled: () => isConfirmDisabled.value,
      isSubmitBlocked: () => selectedCountNumber.value === null,
      onSubmitSuccess: () => store.dispatch('user/getMe', { forceRefresh: true }),
    })

    onMounted(fetchCurrentWashRequest)

    return {
      step: WASH_REQUEST_FIELD_CHANGE_STEP,
      isSubmitting,
      currentStep,
      showWithdrawnError,
      serverErrors,
      originalCount,
      selectedCount,
      countOptions,
      canChangeCount,
      selectedCountNumber,
      isConfirmDisabled,
      goToConfirm,
      backToEdit,
      submit,
    }
  },
})
</script>

<style lang="scss" scoped>
.err-msg-area {
  margin: 30px auto 0;
  text-align: center;
  width: 90%;
}

.submit_bx1,
.submit_bx_l,
.submit_bx_r {
  margin: 12px auto;
}

.count-cell-content {
  display: flex;
  align-items: center;
  min-height: 36px;
}

.form_tab th.form_line_l,
.form_tab td.form_line_r {
  vertical-align: middle;
}

.form_tab th.form_line_l {
  padding: 15px 0;
}

.form_tab td.form_line_r {
  padding: 15px 0;
}

.form_tab th.form_line_l .item2 {
  margin: 0;
  line-height: 36px;
  background-position: 0 50%;
}

.count-cell-content select {
  height: 36px;
  line-height: 36px;
  margin-bottom: 0;
  margin-right: 6px;
  vertical-align: middle;
}

.count-cell-text {
  display: inline-block;
  line-height: 36px;
  vertical-align: middle;
}

.count-row-complete .complete-message {
  margin: 0;
  line-height: 36px;
}

.count-complete-table {
  border-top: 0 !important;
}

.count-complete-table td.count-complete-cell {
  border-bottom: 0;
}

.confirm-button:disabled {
  background-color: #8fd3a9;
  color: #ffffff;
  opacity: 0.8;
  cursor: default;
}

@media (min-width: 769px) {
  .count-cell-content {
    justify-content: flex-start;
  }
}

@media (max-width: 768px) {
  .form_tab th.form_line_l {
    padding: 10px 0 0 0;
  }

  .form_tab td.form_line_r {
    padding: 2px 0 12px 0;
  }

  .count-cell-content {
    min-height: auto;
  }

  .count-row-after th.form_line_l {
    padding-bottom: 5px;
  }

  .count-row-after td.form_line_r {
    padding-top: 8px;
    padding-bottom: 18px;
  }
}
</style>
